/**
 * Account Page - User Account Management
 *
 * Displays user account information and settings.
 */

import { Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { AppHeader } from '@/components/AppHeader';
import { PageBackground } from '@/components/ui/PageLayout';
import { PageSubHeader } from '@/components/ui/PageSubHeader';
import {
  CheckCircle2,
  XCircle,
  Key,
  CreditCard,
  Shield,
  User,
  Copy,
  ExternalLink,
  Building2,
  UserPlus,
  Trash2,
  Zap,
  Plus,
  RotateCcw,
  AlertTriangle,
  LogOut,
} from 'lucide-react';
import { useReportStats } from '@/hooks/useReports';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { PageLoader } from '@/components/ui/PageLoader';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { useAuth, AuthUser } from '@/contexts/AuthContext';
import { useOrgContext } from '@/contexts/OrgContext';
import { useUserInfo, UserInfoData } from '@/hooks/useUserInfo';
import { formatTierName, isPaidTier, isPromotionalTrialTier } from '@/lib/utils';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import {
  useOrganizations,
  useOrganization,
  useCreateOrganization,
  useInviteOrgMember,
  useRemoveOrgMember,
  useDeleteOrganization,
  useOwnedOrgId,
} from '@/hooks/useOrganizations';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

type ConfirmPending = {
  title: string;
  description: string;
  confirmLabel?: string;
  variant?: 'destructive' | 'default';
  action: () => void;
} | null;

// =============================================================================
// PROFILE TAB
// =============================================================================

/**
 * Editable profile information panel (name only; email is read-only).
 *
 * @param user      - JWT claims from useAuth()
 * @param userInfo  - Hydrated user info from /v2/me
 * @param authToken - Bearer token for API calls
 */
function ProfileTab({
  user,
  userInfo,
  authToken,
}: {
  user: AuthUser | null;
  userInfo: UserInfoData | undefined;
  authToken: string;
}) {
  const queryClient = useQueryClient();

  const displayName = user?.name || userInfo?.user?.name || user?.email?.split('@')[0] || '';
  const nameParts = displayName.trim().split(/\s+/);
  const [firstName, setFirstName] = useState(nameParts[0] || '');
  const [lastName, setLastName] = useState(nameParts.slice(1).join(' ') || '');

  const updateMutation = useMutation({
    mutationFn: () =>
      apiClient.updateProfile(firstName.trim(), lastName.trim() || undefined, authToken),
    onSuccess: () => {
      toast.success('Profile updated');
      queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to update profile');
    },
  });

  return (
    <Card className="bg-zinc-800 border-zinc-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center">
          <User className="w-5 h-5 mr-2 text-blue-500" />
          Profile Information
        </CardTitle>
        <CardDescription className="text-zinc-400">
          Your account details and personal information
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Read-only identity fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium text-zinc-400">Email Address</label>
            <p className="text-white mt-1">
              {user?.email || userInfo?.user?.email || 'Not available'}
            </p>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-400">Email Verification</label>
            <div className="flex items-center gap-2 mt-1">
              {user?.emailVerified ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-green-400">Verified</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-yellow-500" />
                  <span className="text-yellow-400">Not verified</span>
                </>
              )}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-400">User ID</label>
            <p className="text-white mt-1 font-mono text-sm">{user?.sub || userInfo?.user?.id}</p>
          </div>
        </div>

        {/* Editable name fields */}
        <div className="border-t border-zinc-700 pt-4 space-y-4">
          <p className="text-sm font-medium text-zinc-300">Display Name</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-xs text-zinc-400">First Name</label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name"
                className="bg-zinc-900 border-zinc-600 text-white placeholder-zinc-500"
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-zinc-400">Last Name</label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Last name"
                className="bg-zinc-900 border-zinc-600 text-white placeholder-zinc-500"
              />
            </div>
          </div>
          <Button
            onClick={() => updateMutation.mutate()}
            disabled={updateMutation.isPending || !firstName.trim()}
            className="bg-blue-600 hover:bg-blue-500 text-white"
          >
            {updateMutation.isPending ? 'Saving…' : 'Save Name'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// API KEYS TAB
// =============================================================================

/**
 * Full API key management panel.
 *
 * @param authToken     - Bearer token for API calls
 * @param subscriptions - User's active Stripe subscriptions (may be empty)
 */
function ApiKeysTab({ authToken }: { authToken: string }) {
  const queryClient = useQueryClient();
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState<{ id: string; key: string } | null>(null);
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmPending>(null);
  const [, copy] = useCopyToClipboard();

  // Key list from the dedicated /v2/keys endpoint
  const { data, isLoading } = useQuery({
    queryKey: ['apiKeys', authToken],
    queryFn: () => apiClient.listKeys(authToken),
    enabled: !!authToken,
    staleTime: 30_000,
  });

  const keys = data?.keys ?? [];
  const canCreate = data?.canCreateMore ?? true;

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['apiKeys', authToken] });

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.createKey(authToken, {
        name: newKeyName.trim() || undefined,
      }),
    onSuccess: (result) => {
      setRevealedKey({ id: result.id, key: result.key });
      setNewKeyName('');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create key'),
  });

  const rotateMutation = useMutation({
    mutationFn: (keyId: string) => apiClient.rotateKey(authToken, keyId),
    onSuccess: (result) => {
      setRevealedKey({ id: result.id, key: result.key });
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to rotate key'),
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId: string) => apiClient.revokeKey(authToken, keyId),
    onSuccess: () => {
      toast.success('API key revoked');
      invalidate();
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to revoke key'),
  });

  return (
    <div className="space-y-6">
      {/* One-time key reveal banner */}
      {revealedKey && (
        <Card className="bg-emerald-900/20 border-emerald-500/40">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-emerald-300 text-sm font-semibold mb-1">
                  Copy your API key now — it will not be shown again
                </p>
                <code className="block bg-zinc-900 text-green-400 px-3 py-2 rounded text-xs font-mono break-all">
                  {revealedKey.key}
                </code>
              </div>
              <Button
                size="sm"
                onClick={() => {
                  copy(revealedKey.key);
                  toast.success('Copied!');
                }}
                className="bg-emerald-700 hover:bg-emerald-600 text-white flex-shrink-0"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setRevealedKey(null)}
              className="mt-2 text-zinc-400 hover:text-zinc-200 text-xs"
            >
              I've saved it — dismiss
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Create new key */}
      <Card className="bg-zinc-800 border-zinc-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Key className="w-5 h-5 text-green-500" />
              API Keys
            </span>
            <Badge variant="outline" className="text-zinc-400 border-zinc-600 text-xs">
              {keys.length} / {data?.limit ?? 5}
            </Badge>
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Use API keys to authenticate programmatic access and MCP integrations. Each key can be
            scoped to a specific subscription.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Create form */}
          {canCreate && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder="Key name (e.g. Claude Desktop)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="bg-zinc-900 border-zinc-700 text-white placeholder-zinc-500 flex-1"
                maxLength={64}
              />
              <Button
                onClick={() => createMutation.mutate()}
                disabled={createMutation.isPending}
                className="bg-emerald-700 hover:bg-emerald-600 text-white flex-shrink-0"
              >
                <Plus className="w-4 h-4 mr-1" />
                {createMutation.isPending ? 'Creating…' : 'New Key'}
              </Button>
            </div>
          )}
          {!canCreate && (
            <p className="text-xs text-amber-400">
              Maximum keys reached. Revoke an existing key before creating a new one.
            </p>
          )}

          {/* Key list */}
          {isLoading && <p className="text-zinc-400 text-sm">Loading keys…</p>}
          {!isLoading && keys.length === 0 && (
            <p className="text-zinc-500 text-sm">No API keys yet. Create one above.</p>
          )}
          <div className="space-y-2">
            {keys.map((k) => (
              <div
                key={k.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 rounded-lg bg-zinc-900 border border-zinc-700"
              >
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{k.name}</p>
                  <p className="text-zinc-500 text-xs font-mono mt-0.5">{k.prefix}</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {k.expiresAt && (
                      <span className="text-xs text-zinc-500">
                        expires {new Date(k.expiresAt).toLocaleDateString()}
                      </span>
                    )}
                    {k.lastUsedAt && (
                      <span className="text-xs text-zinc-500">
                        last used {new Date(k.lastUsedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setPendingConfirm({
                        title: 'Rotate API Key',
                        description: `Rotating "${k.name}" generates a new key and immediately invalidates the current one. Any integrations using the old key must be updated.`,
                        confirmLabel: 'Rotate Key',
                        action: () => rotateMutation.mutate(k.id),
                      })
                    }
                    disabled={rotateMutation.isPending}
                    title="Rotate key"
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700 text-xs"
                  >
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                    Rotate
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() =>
                      setPendingConfirm({
                        title: 'Revoke API Key',
                        description: `Revoking "${k.name}" permanently invalidates it. Any integrations using this key will stop working immediately. This cannot be undone.`,
                        confirmLabel: 'Revoke Key',
                        action: () => revokeMutation.mutate(k.id),
                      })
                    }
                    disabled={revokeMutation.isPending}
                    title="Revoke key"
                    className="border-red-800 text-red-400 hover:bg-red-900/30 text-xs"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1" />
                    Revoke
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs text-zinc-500 pt-2 border-t border-zinc-700">
            API keys authenticate via <code className="text-zinc-300">X-API-Key</code> header or{' '}
            <code className="text-zinc-300">Authorization: Bearer gr_live_…</code>. Never commit
            keys to source control.
          </p>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!pendingConfirm}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null);
        }}
        title={pendingConfirm?.title ?? ''}
        description={pendingConfirm?.description ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel}
        variant={pendingConfirm?.variant ?? 'destructive'}
        onConfirm={() => {
          pendingConfirm?.action();
          setPendingConfirm(null);
        }}
      />
    </div>
  );
}

export default function AccountPage() {
  useDocumentTitle('Account');
  const { authenticated, loading: authLoading, user, token } = useAuth();
  const { activeOrgId, setActiveOrg } = useOrgContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'profile';
  const { data: userInfo, isLoading } = useUserInfo();
  const { data: statsData } = useReportStats();
  const [inviteEmail, setInviteEmail] = useState('');
  const [newOrgName, setNewOrgName] = useState('');
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmPending>(null);
  const pc = (cfg: NonNullable<ConfirmPending>) => setPendingConfirm(cfg);

  const authToken = token ?? '';
  const subscriptionStatus = userInfo?.subscription?.tier;
  const hasActiveSubscription = isPaidTier(subscriptionStatus);
  const isPromotionalWindowTrial = isPromotionalTrialTier(subscriptionStatus);
  const queryClient = useQueryClient();

  // Org hooks
  const currentUserId = user?.sub ?? '';
  const { data: orgs = [], isLoading: orgsLoading } = useOrganizations(authToken);
  const { data: ownedOrgId } = useOwnedOrgId(authToken);
  // Always fetch detail for the org the user owns (includes seats + members).
  const { data: ownedOrgDetail } = useOrganization(authToken, ownedOrgId ?? '');
  // Orgs where this user is a member but NOT the owner.
  const memberOrgs = orgs.filter((o) => o.id !== ownedOrgId);
  const createOrgMutation = useCreateOrganization(authToken);
  const inviteMutation = useInviteOrgMember(authToken, ownedOrgId ?? '');
  const removeMemberMutation = useRemoveOrgMember(authToken);
  const deleteOrgMutation = useDeleteOrganization(authToken);

  // When dissolving the owned org, clear it from the active billing context if selected.
  const dissolveOrg = (orgId: string) => {
    deleteOrgMutation.mutate(orgId, {
      onSuccess: () => {
        if (activeOrgId === orgId) setActiveOrg(null);
      },
    });
  };

  /** Open Stripe Customer Portal — handles payment methods, invoices, and plan changes */
  const portalMutation = useMutation({
    mutationFn: () =>
      apiClient.getBillingPortal(authToken, `${window.location.origin}/account?tab=subscription`),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to open billing portal');
    },
  });

  /** Cancel subscription at period end */
  const cancelMutation = useMutation({
    mutationFn: () => apiClient.cancelSubscription(authToken),
    onSuccess: (data) => {
      toast.success(data.message || 'Subscription will cancel at period end');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel subscription');
    },
  });

  /**
   * Immediately cancel (sandbox only — only rendered when import.meta.env.DEV).
   * Invalidates the userInfo query so the tier badge updates without a refresh.
   */
  const cancelImmediateMutation = useMutation({
    mutationFn: () => apiClient.cancelSubscriptionImmediately(authToken),
    onSuccess: (data) => {
      toast.success(data.message || 'Subscription immediately canceled');
      queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Immediate cancel failed');
    },
  });

  /** Switch active subscription when user holds multiple plans */
  // Removed: one subscription per user.

  if (!authenticated && !authLoading) return <Navigate to="/login" replace />;
  if (authLoading || isLoading) return <PageLoader message="Loading account..." />;

  return (
    <PageBackground>
      <AppHeader />
      <PageSubHeader
        title="Account Settings"
        subtitle="Manage your account preferences and settings"
      />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Tabs
            value={currentTab}
            onValueChange={(value) => setSearchParams({ tab: value })}
            className="space-y-6"
          >
            <TabsList className="grid w-full grid-cols-5 bg-zinc-800 border border-zinc-700">
              <TabsTrigger
                value="profile"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </TabsTrigger>
              <TabsTrigger
                value="api"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                <Key className="w-4 h-4 mr-2" />
                API Access
              </TabsTrigger>
              <TabsTrigger
                value="subscription"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Subscription
              </TabsTrigger>
              <TabsTrigger
                value="organizations"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                <Building2 className="w-4 h-4 mr-2" />
                Orgs
              </TabsTrigger>
              <TabsTrigger
                value="security"
                className="data-[state=active]:bg-zinc-700 data-[state=active]:text-white"
              >
                <Shield className="w-4 h-4 mr-2" />
                Security
              </TabsTrigger>
            </TabsList>

            <TabsContent value="profile" className="space-y-6">
              <ProfileTab user={user} userInfo={userInfo} authToken={authToken} />
            </TabsContent>

            <TabsContent value="api" className="space-y-6">
              <ApiKeysTab authToken={authToken} />
            </TabsContent>

            <TabsContent value="subscription" className="space-y-6">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <CreditCard className="w-5 h-5 mr-2 text-purple-500" />
                    Subscription & Billing
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Current plan, usage limits, and billing information
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-zinc-400">Current Plan</label>
                      <p className="text-white mt-1 text-lg font-medium">
                        {formatTierName(
                          userInfo?.subscription?.tier,
                          userInfo?.subscription?.tierName
                        )}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-zinc-400">Account Status</label>
                      <p className="text-white mt-1 capitalize">
                        {userInfo?.user?.status || 'Active'}
                      </p>
                    </div>
                    {userInfo?.subscription?.seats != null && (
                      <div>
                        <label className="text-sm font-medium text-zinc-400">Seats</label>
                        <p className="text-white mt-1">
                          {userInfo.subscription.seats === -1
                            ? 'Unlimited'
                            : userInfo.subscription.seats}
                        </p>
                      </div>
                    )}
                    <div>
                      <label className="text-sm font-medium text-zinc-400">Total Scans</label>
                      <p className="text-white mt-1">
                        {statsData?.totalScans ?? userInfo?.limits?.scansUsed ?? 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-zinc-400">This Month</label>
                      <p className="text-white mt-1">
                        {statsData?.scansThisMonth ?? 0}
                        {userInfo?.limits?.scansPerMonth && userInfo.limits.scansPerMonth > 0
                          ? ` / ${userInfo.limits.scansPerMonth}`
                          : ''}
                      </p>
                    </div>
                    {(() => {
                      // Show trial/promotion end date from actual subscription data
                      const trialEndStr = userInfo?.subscription?.trialEndsAt;
                      const periodEndStr = userInfo?.subscription?.currentPeriodEnd;
                      const status = userInfo?.subscription?.status;

                      if (isPromotionalWindowTrial && trialEndStr) {
                        const promoEnd = new Date(trialEndStr);
                        const now = new Date();
                        const daysRemaining = Math.ceil(
                          (promoEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                        );
                        return (
                          <div>
                            <label className="text-sm font-medium text-zinc-400">Promotion Ends</label>
                            <p className="text-white mt-1">
                              {promoEnd.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' })}
                              {daysRemaining > 0 && (
                                <span className="ml-2 text-yellow-400 font-medium">
                                  ({daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining)
                                </span>
                              )}
                            </p>
                          </div>
                        );
                      }

                      if (trialEndStr && status === 'trialing') {
                        const trialEnd = new Date(trialEndStr);
                        const now = new Date();
                        const daysRemaining = Math.ceil(
                          (trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
                        );
                        return (
                          <div>
                            <label className="text-sm font-medium text-zinc-400">Trial Ends</label>
                            <p className="text-white mt-1">
                              {trialEnd.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' })}
                              {daysRemaining > 0 && (
                                <span className="ml-2 text-yellow-400 font-medium">
                                  ({daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining)
                                </span>
                              )}
                            </p>
                          </div>
                        );
                      }

                      if (periodEndStr) {
                        return (
                          <div>
                            <label className="text-sm font-medium text-zinc-400">Current Period Ends</label>
                            <p className="text-white mt-1">
                              {new Date(periodEndStr).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' })}
                            </p>
                          </div>
                        );
                      }

                      return null;
                    })()}
                    {userInfo?.subscription?.upcomingInvoice && (
                      <div>
                        <label className="text-sm font-medium text-zinc-400">Next Payment</label>
                        <p className="text-white mt-1">
                          ${(userInfo.subscription.upcomingInvoice.amountDue / 100).toFixed(2)}
                          {userInfo.subscription.upcomingInvoice.dueDate && (
                            <span className="text-zinc-400 ml-1">
                              on {new Date(userInfo.subscription.upcomingInvoice.dueDate).toLocaleDateString()}
                            </span>
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="border-t border-zinc-700 pt-4 flex flex-wrap gap-3">
                    {hasActiveSubscription ? (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => portalMutation.mutate()}
                          disabled={portalMutation.isPending}
                          className="border-zinc-600 bg-zinc-800 text-white hover:bg-zinc-700"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          {portalMutation.isPending ? 'Opening…' : 'Manage Subscription'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => (window.location.href = '/pricing')}
                          className="border-zinc-600 bg-zinc-800 text-zinc-300 hover:bg-zinc-700"
                        >
                          <CreditCard className="w-4 h-4 mr-2" />
                          Change Plan
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() =>
                            pc({
                              title: 'Cancel Subscription',
                              description:
                                'You will retain access until the end of your current billing period. After that, your account reverts to the free tier.',
                              confirmLabel: 'Yes, Cancel',
                              action: () => cancelMutation.mutate(),
                            })
                          }
                          disabled={cancelMutation.isPending}
                          className="border-red-600 bg-red-600/10 text-red-400 hover:bg-red-600/20"
                        >
                          {cancelMutation.isPending ? 'Canceling…' : 'Cancel Subscription'}
                        </Button>
                        {/* Sandbox-only: immediately cancel for testing the full cancel → downgrade flow */}
                        {import.meta.env.DEV && (
                          <Button
                            variant="outline"
                            onClick={() =>
                              pc({
                                title: '[Sandbox] Cancel Immediately',
                                description:
                                  'This fires the customer.subscription.deleted event locally and reverts your tier to trial right now.',
                                confirmLabel: 'Cancel Now',
                                action: () => cancelImmediateMutation.mutate(),
                              })
                            }
                            disabled={cancelImmediateMutation.isPending}
                            className="border-orange-500 bg-orange-500/10 text-orange-300 hover:bg-orange-500/20"
                          >
                            <Zap className="w-4 h-4 mr-2" />
                            {cancelImmediateMutation.isPending
                              ? 'Canceling…'
                              : 'Cancel Now (Sandbox)'}
                          </Button>
                        )}
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => (window.location.href = '/pricing')}
                        className="border-zinc-600 bg-zinc-800 text-white hover:bg-zinc-700"
                      >
                        <CreditCard className="w-4 h-4 mr-2" />
                        {subscriptionStatus === 'trial' ? 'Upgrade Plan' : 'Start Free'}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="organizations" className="space-y-6">
              {/* ── YOUR ORGANIZATION ─────────────────────────────────── */}
              <Card className="bg-zinc-800 border-zinc-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Building2 className="w-5 h-5 mr-2 text-blue-500" />
                    Your Organization
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Each account can own one organization. Your subscription's seat count sets how
                    many members you can invite. Use the org selector in the header to bill scans
                    against your org's subscription.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {orgsLoading ? (
                    <p className="text-zinc-400 text-sm">Loading…</p>
                  ) : ownedOrgId && ownedOrgDetail ? (
                    <>
                      {/* Seat usage */}
                      <div className="grid grid-cols-3 gap-3">
                        <div className="text-center bg-zinc-900 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">
                            {ownedOrgDetail.seatsUsed}
                          </p>
                          <p className="text-xs text-zinc-400 mt-1">Used</p>
                        </div>
                        <div className="text-center bg-zinc-900 rounded-lg p-3">
                          <p className="text-2xl font-bold text-white">
                            {ownedOrgDetail.seatsTotal === -1 ? '∞' : ownedOrgDetail.seatsTotal}
                          </p>
                          <p className="text-xs text-zinc-400 mt-1">Total</p>
                        </div>
                        <div className="text-center bg-zinc-900 rounded-lg p-3">
                          <p className="text-2xl font-bold text-green-400">
                            {ownedOrgDetail.seatsAvailable === -1
                              ? '∞'
                              : ownedOrgDetail.seatsAvailable}
                          </p>
                          <p className="text-xs text-zinc-400 mt-1">Available</p>
                        </div>
                      </div>

                      {/* Invite */}
                      <div className="flex gap-2">
                        <Input
                          type="email"
                          name="invite-email"
                          autoComplete="email"
                          placeholder="Invite by email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="bg-zinc-900 border-zinc-600 text-white placeholder-zinc-500 flex-1"
                        />
                        <Button
                          onClick={() => {
                            if (!inviteEmail.trim()) return;
                            inviteMutation.mutate(
                              { email: inviteEmail.trim() },
                              { onSuccess: () => setInviteEmail('') }
                            );
                          }}
                          disabled={inviteMutation.isPending || !inviteEmail.trim()}
                          className="bg-green-600 hover:bg-green-500 text-white shrink-0"
                        >
                          <UserPlus className="w-4 h-4 sm:mr-1.5" />
                          <span className="hidden sm:inline">
                            {inviteMutation.isPending ? 'Sending…' : 'Invite'}
                          </span>
                        </Button>
                      </div>

                      {/* Member list */}
                      <div className="space-y-2">
                        {ownedOrgDetail.members.map((member) => (
                          <div
                            key={member.id}
                            className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-900 border border-zinc-700"
                          >
                            <div>
                              <p className="text-white text-sm font-medium">
                                {member.firstName || member.lastName
                                  ? `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim()
                                  : member.username}
                              </p>
                              <p className="text-zinc-400 text-xs">{member.email}</p>
                            </div>
                            {member.id !== user?.sub && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  pc({
                                    title: 'Remove Member',
                                    description: `Remove ${member.email} from this organization? They will lose access immediately.`,
                                    confirmLabel: 'Remove',
                                    action: () =>
                                      removeMemberMutation.mutate({
                                        orgId: ownedOrgId,
                                        userId: member.id,
                                      }),
                                  })
                                }
                                disabled={removeMemberMutation.isPending}
                                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>

                      {/* Leave / delete */}
                      <div className="border-t border-zinc-700 pt-3">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={deleteOrgMutation.isPending}
                          onClick={() => {
                            const memberCount = ownedOrgDetail.members.length;
                            const othersCount = memberCount - 1; // exclude the owner
                            const othersLine =
                              othersCount > 0
                                ? ` ${othersCount} other ${othersCount === 1 ? 'member' : 'members'} will immediately lose their seat.`
                                : '';
                            pc({
                              title: 'Dissolve Organization',
                              description: `Dissolving "${ownedOrgDetail.displayName ?? ownedOrgDetail.name}" permanently deletes it and revokes all memberships.${othersLine} You will have no organization until you create a new one. This cannot be undone.`,
                              confirmLabel: 'Dissolve Org',
                              action: () => dissolveOrg(ownedOrgId),
                            });
                          }}
                          className="border-red-800 text-red-400 hover:bg-red-900/20 text-xs"
                        >
                          <LogOut className="w-3.5 h-3.5 mr-1.5" />
                          Leave &amp; Dissolve Org
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* Create form — only shown when user owns no org */
                    <div className="flex gap-2">
                      <Input
                        placeholder="Organization name"
                        value={newOrgName}
                        onChange={(e) => setNewOrgName(e.target.value)}
                        className="bg-zinc-900 border-zinc-600 text-white placeholder-zinc-500 flex-1"
                      />
                      <Button
                        onClick={() => {
                          if (!newOrgName.trim()) return;
                          createOrgMutation.mutate(
                            { name: newOrgName.trim() },
                            { onSuccess: () => setNewOrgName('') }
                          );
                        }}
                        disabled={createOrgMutation.isPending || !newOrgName.trim()}
                        className="bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        {createOrgMutation.isPending ? 'Creating…' : 'Create Org'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ── TEAM MEMBERSHIPS ──────────────────────────────────── */}
              {memberOrgs.length > 0 && (
                <Card className="bg-zinc-800 border-zinc-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center text-base">
                      <Building2 className="w-4 h-4 mr-2 text-zinc-400" />
                      Team Memberships
                    </CardTitle>
                    <CardDescription className="text-zinc-400">
                      Organizations you belong to. Activate one to bill scans against that org's
                      subscription instead of your personal quota.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {memberOrgs.map((org) => (
                      <div
                        key={org.id}
                        className="flex items-center justify-between p-3 rounded-lg bg-zinc-900 border border-zinc-700"
                      >
                        <div>
                          <p className="text-white text-sm font-medium">
                            {org.displayName ?? org.name}
                          </p>
                          {activeOrgId === org.id ? (
                            <p className="text-xs text-green-400 mt-0.5">Active billing context</p>
                          ) : (
                            <p className="text-xs text-zinc-500 mt-0.5">
                              Scans billed to personal
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {activeOrgId !== org.id && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setActiveOrg(org.id)}
                              className="border-blue-700 text-blue-400 hover:bg-blue-900/20 text-xs"
                            >
                              <Building2 className="w-3.5 h-3.5 mr-1" />
                              Use org billing
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            title="Leave organization"
                            disabled={removeMemberMutation.isPending}
                            onClick={() =>
                              pc({
                                title: 'Leave Organization',
                                description: `Leave "${org.displayName ?? org.name}"? You will lose access to this org's billing context immediately.`,
                                confirmLabel: 'Leave',
                                action: () =>
                                  removeMemberMutation.mutate({
                                    orgId: org.id,
                                    userId: currentUserId,
                                  }),
                              })
                            }
                            className="border-zinc-600 text-zinc-400 hover:border-red-600 hover:text-red-400 hover:bg-red-900/20 text-xs"
                          >
                            <LogOut className="w-3.5 h-3.5 mr-1" />
                            Leave
                          </Button>
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="security" className="space-y-6">
              <Card className="bg-zinc-800 border-zinc-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center">
                    <Shield className="w-5 h-5 mr-2 text-red-500" />
                    Security Settings
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Manage your account security and authentication preferences
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-zinc-400">
                      Authentication Method
                    </label>
                    <p className="text-white mt-1">Magic Link (Passwordless)</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Your account uses secure magic links sent to your email for authentication.
                    </p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-zinc-400">Session Management</label>
                    <p className="text-white mt-1">Automatic session management</p>
                    <p className="text-xs text-zinc-400 mt-1">
                      Your session is automatically managed and will expire when you log out or
                      after extended inactivity.
                    </p>
                  </div>

                  <div className="border-t border-zinc-700 pt-4">
                    <Button
                      variant="outline"
                      onClick={() =>
                        pc({
                          title: 'Log Out of All Sessions',
                          description:
                            'This will end your current session. You will need to sign in again.',
                          confirmLabel: 'Log Out',
                          action: () => {
                            window.location.href = '/login';
                          },
                        })
                      }
                      className="border-red-600 bg-red-600/10 text-red-400 hover:bg-red-600/20"
                    >
                      Log Out of All Sessions
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <ConfirmDialog
        open={!!pendingConfirm}
        onOpenChange={(open) => {
          if (!open) setPendingConfirm(null);
        }}
        title={pendingConfirm?.title ?? ''}
        description={pendingConfirm?.description ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel}
        variant={pendingConfirm?.variant ?? 'destructive'}
        onConfirm={() => {
          pendingConfirm?.action();
          setPendingConfirm(null);
        }}
      />
    </PageBackground>
  );
}
